const TRAIL_KEY = 'category-breadcrumb-trail';
const PENDING_KEY = 'category-breadcrumb-pending';

/**
 * @param {string} url
 * @returns {string}
 */
function pathnameOf(url) {
  try {
    const value = new URL(url, window.location.origin).pathname;
    if (value.length > 1 && value.endsWith('/')) return value.slice(0, -1);
    return value;
  } catch {
    return '';
  }
}

/**
 * @param {string | null | undefined} value
 * @returns {{ title: string, url: string }[]}
 */
function readJson(value) {
  try {
    const parsed = JSON.parse(value || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item) => item && typeof item.title === 'string' && typeof item.url === 'string');
  } catch {
    return [];
  }
}

/**
 * @returns {{ title: string, url: string }[]}
 */
function readStoredTrail() {
  try {
    return readJson(sessionStorage.getItem(TRAIL_KEY));
  } catch {
    return [];
  }
}

/**
 * Prefer the collection breadcrumb nav. Fall back to the document page type
 * so a product click can still mark the menu trail.
 * @returns {string}
 */
function currentCollectionPath() {
  const nav = document.querySelector('.breadcrumbs');
  if (nav instanceof HTMLElement && nav.dataset.pageType === 'collection') {
    return pathnameOf(nav.dataset.pageUrl || window.location.href);
  }

  if (document.body?.dataset.pageType === 'collection') {
    return pathnameOf(window.location.href);
  }

  return '';
}

function captureClicks() {
  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = event.target.closest('a[href]');
      if (!(link instanceof HTMLAnchorElement)) return;
      if (link.target === '_blank') return;

      const trailAttr = link.getAttribute('data-breadcrumb-trail');
      if (trailAttr) {
        try {
          sessionStorage.setItem(TRAIL_KEY, JSON.stringify(withoutShop(readJson(trailAttr), null)));
          sessionStorage.removeItem(PENDING_KEY);
        } catch {
          // sessionStorage can be unavailable.
        }
        return;
      }

      const button = event.target.closest('button, [role="button"]');
      if (button && link.contains(button)) return;

      const currentPath = currentCollectionPath();
      if (!currentPath) return;

      const productPath = pathnameOf(link.href);
      if (!productPath.includes('/products/')) return;

      const trail = readStoredTrail();
      const last = trail[trail.length - 1];
      if (!last || pathnameOf(last.url) !== currentPath) return;

      try {
        sessionStorage.setItem(PENDING_KEY, productPath);
      } catch {
        // sessionStorage can be unavailable.
      }
    },
    true
  );
}

/**
 * @param {HTMLElement} nav
 * @param {{ title: string, url: string }[]} trail
 */
function withoutHome(nav, trail) {
  const homePath = pathnameOf(nav.dataset.homeUrl || '/');
  if (trail.length && pathnameOf(trail[0].url) === homePath) return trail.slice(1);
  return trail.slice();
}

/**
 * Shop section handle from the breadcrumb nav, or from the body when the nav
 * is not on the page (the homepage menu still stores trails).
 * @param {HTMLElement | null | undefined} nav
 * @returns {string}
 */
function shopHandle(nav) {
  const scoped = nav instanceof HTMLElement ? nav.dataset.shopHandle : '';
  const value = scoped || document.body?.dataset.shopHandle || '';
  return value.trim().toLowerCase();
}

/**
 * The shop section parent is a menu container, not a destination in the trail.
 * Match the menu link handle from data-shop-handle, not the link title.
 * @param {{ title: string, url: string, handle?: string }[]} trail
 * @param {HTMLElement | null | undefined} nav
 */
function withoutShop(trail, nav) {
  const handle = shopHandle(nav);
  if (!trail.length || !handle) return trail;
  const crumbHandle = typeof trail[0].handle === 'string' ? trail[0].handle.trim().toLowerCase() : '';
  if (crumbHandle === handle) return trail.slice(1);
  return trail;
}

/**
 * @param {{ title: string, url: string }[]} left
 * @param {{ title: string, url: string }[]} right
 */
function sameTrail(left, right) {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index].title !== right[index].title) return false;
    if (pathnameOf(left[index].url) !== pathnameOf(right[index].url)) return false;
  }
  return true;
}

/**
 * @param {HTMLElement} nav
 * @param {{ title: string, url: string }[]} menuTrail
 */
function store(menuTrail) {
  try {
    sessionStorage.setItem(TRAIL_KEY, JSON.stringify(menuTrail));
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // sessionStorage can be unavailable.
  }
}

/**
 * @param {HTMLElement} nav
 * @param {{ title: string, url: string }[]} menuTrail
 */
function renderTrail(nav, menuTrail) {
  const list = nav.querySelector('.breadcrumbs__list');
  const separatorTemplate = nav.querySelector('template');
  if (!list) return;

  const homePath = pathnameOf(nav.dataset.homeUrl || '/');
  const display = menuTrail.slice();
  if (!display.length || pathnameOf(display[0].url) !== homePath) {
    display.unshift({
      title: nav.dataset.homeTitle || 'Home',
      url: nav.dataset.homeUrl || '/',
    });
  }

  list.replaceChildren();
  display.forEach((crumb, index) => {
    const item = document.createElement('li');
    item.className = 'breadcrumbs__item';
    const isCurrent = index === display.length - 1;
    const label = document.createElement('span');
    label.className = 'breadcrumbs__label';
    label.textContent = crumb.title;

    if (isCurrent) {
      const current = document.createElement('span');
      current.className = 'breadcrumbs__current';
      current.setAttribute('aria-current', 'page');
      current.append(label);
      item.append(current);
    } else {
      const link = document.createElement('a');
      link.className = 'breadcrumbs__link';
      link.href = crumb.url;
      const menuIndex = pathnameOf(display[0].url) === homePath ? index - 1 : index;
      if (menuIndex >= 0) {
        link.setAttribute('data-breadcrumb-trail', JSON.stringify(menuTrail.slice(0, menuIndex + 1)));
      }
      link.append(label);
      if (separatorTemplate instanceof HTMLTemplateElement && separatorTemplate.content.firstElementChild) {
        link.append(separatorTemplate.content.firstElementChild.cloneNode(true));
      }
      item.append(link);
    }

    list.append(item);
  });
}

/**
 * @param {HTMLElement} nav
 */
function applyStoredTrail(nav) {
  if (nav.closest('#quick-add-dialog')) return;

  const currentPath = pathnameOf(nav.dataset.pageUrl || window.location.href);
  const menuTrail = withoutShop(withoutHome(nav, readStoredTrail()), nav);
  const lastCrumb = menuTrail[menuTrail.length - 1];

  if (lastCrumb && pathnameOf(lastCrumb.url) === currentPath) {
    store(menuTrail);
    if (!sameTrail(menuTrail, readJson(nav.getAttribute('data-server-trail')))) renderTrail(nav, menuTrail);
    return;
  }

  let pending = '';
  try {
    pending = sessionStorage.getItem(PENDING_KEY) || '';
  } catch {
    pending = '';
  }

  if (pending && pending === currentPath && menuTrail.length) {
    const pendingTrail = menuTrail.concat({
      title: nav.dataset.pageTitle || '',
      url: nav.dataset.pageUrl || window.location.pathname,
    });
    if (!sameTrail(pendingTrail, readJson(nav.getAttribute('data-server-trail')))) {
      renderTrail(nav, pendingTrail);
    }
  }
}

function renderBreadcrumbs() {
  document.querySelectorAll('.breadcrumbs').forEach((nav) => {
    if (nav instanceof HTMLElement) applyStoredTrail(nav);
  });
}

captureClicks();
renderBreadcrumbs();
document.addEventListener('shopify:section:load', renderBreadcrumbs);
document.addEventListener('variant:update', () => {
  queueMicrotask(renderBreadcrumbs);
});
